import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	computed,
	DOCUMENT,
	ElementRef,
	EventEmitter,
	HostListener,
	inject,
	Input,
	input,
	OnChanges,
	Output,
	PLATFORM_ID,
	ViewChild,
} from '@angular/core';
import { RouterModule } from '@angular/router';

import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';

import { LinkComponent } from '@valk-nx/components/ui-link/src/lib/link';
import { Link } from '@valk-nx/components/ui-link/src/lib/link.interface';
import type { LinkListItem } from '@valk-nx/components/ui-link-list/src/lib/link-list.interface';
import { cn, LANGUAGE, Language } from '@valk-nx/core/lib/core';
import { debounceFn } from '@valk-nx/helpers/lib/debounce/debounce.helper';
import { GeneralHelper } from '@valk-nx/helpers/lib/general/general.helper';

import {
	CtaGroupResolved,
	CtaItem,
	HeaderMenuItem,
	isCtaGroupResolved,
	MetaMenu,
} from '../header.interface';

import { BottomSheetComponent } from './bottom-sheet/bottom-sheet';

const duration = 300;

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: `vp-header-mobile`,
	templateUrl: './header-mobile.html',
	imports: [
		AngularSvgIconModule,
		CommonModule,
		LinkComponent,
		RouterModule,
		TranslatePipe,
		BottomSheetComponent,
	],
})
export class HeaderMobileComponent implements OnChanges, AfterViewInit {
	translate = inject(TranslateService);
	private readonly platformId = inject(PLATFORM_ID);
	private readonly document = inject<Document>(DOCUMENT);
	private readonly rootLanguage = inject<Language>(LANGUAGE);
	private readonly cd = inject(ChangeDetectorRef);

	@Input({ required: true }) menuItems: HeaderMenuItem[];
	metaMenu = input.required<MetaMenu>();
	cta = input<CtaItem | CtaGroupResolved>();
	@Input() title: string;
	@Input() hideMenu = false;

	@Output() languageSwitch = new EventEmitter<string>();

	@ViewChild('mobileHeaderEl')
	mobileHeaderElement: ElementRef<HTMLInputElement>;
	@ViewChild('mobileMenuEl') mobileMenuElement: ElementRef<HTMLInputElement>;
	@ViewChild('metaMenuEl') metaMenuElement: ElementRef<HTMLInputElement>;
	@ViewChild('languageMenuEl')
	languageMenuElement: ElementRef<HTMLInputElement>;
	@ViewChild('ctaMenuEl')
	ctaMenuElement: ElementRef<HTMLInputElement>;
	isNegativeHeader = input<boolean>();

	isCtaGroupResolved = computed<CtaGroupResolved | undefined>(() => {
		const cta = this.cta();
		if (!cta) return undefined;
		return isCtaGroupResolved(cta) ? cta : undefined;
	});

	isCtaItem = computed<CtaItem | undefined>(() => {
		const cta = this.cta();
		if (!cta) return undefined;
		return isCtaGroupResolved(cta) ? undefined : cta;
	});

	languageOptionsForLinkList = computed<LinkListItem[] | undefined>(() => {
		const languageList = this.metaMenu().languages?.options;
		if (!languageList) return undefined;

		return languageList.map((option) => ({
			...languageList,
			text: option.selectLabel,
			url: option.value,
			targetSelf: true,
		}));
	});

	menuAnimation: KeyframeAnimationOptions = {
		duration,
		easing: 'ease-out',
		fill: 'both',
	};

	menuIsOpen: boolean;
	languageIsOpen: boolean;
	ctaIsOpen: boolean;
	logoLink: Link;
	mobileHeaderDeltaY: number;
	mobileMenuDeltaY: number;
	metaMenuDeltaY: number;
	languageMenuDeltaY: number;
	ctaMenuDeltaY = 0;
	topBannerHeight: number;
	private backgroundTimeoutId: ReturnType<typeof setTimeout> | null = null;

	language: string;
	showBackground = false;
	mobileMenuAnimation: Animation | null = null;
	cn = cn;

	private readonly debouncedSetupAnimationPositions = debounceFn(() =>
		this.setupAnimationPositions(),
	);

	constructor() {
		this.language =
			this.translate?.getCurrentLang() ??
			this.translate?.getFallbackLang();
	}

	ngOnChanges() {
		this.logoLink = {
			title: 'home',
			url:
				this.translate.getCurrentLang() === this.rootLanguage
					? '/'
					: `/${this.translate.getCurrentLang()}`,
		};

		this.updateBackgroundState();
	}

	ngAfterViewInit() {
		this.setupAnimationPositions();
	}

	@HostListener('window:resize')
	updateAfterResize() {
		this.debouncedSetupAnimationPositions();
	}

	/* v8 ignore start */
	setupAnimationPositions() {
		// Always close menu if it is open to prevent unwanted interactions
		// Without detectChanges, the menu will not properly close when you close devtools or switch between desktop and mobile views
		if (this.languageIsOpen) {
			this.toggleLanguageMenu();
			this.cd.detectChanges();
		}
		if (this.menuIsOpen) {
			this.toggleMenu();
			this.cd.detectChanges();
		}
		if (isPlatformBrowser(this.platformId) && window.innerWidth < 1024) {
			// Save the current state so we can reapply it after the animation
			const initialMenuIsOpen = this.menuIsOpen;
			const initialLanguageIsOpen = this.languageIsOpen;

			// Get the nativeElements of the elements we want to animate
			const mobileHeader = this.mobileHeaderElement.nativeElement;
			const mobileMenu = this.mobileMenuElement.nativeElement;
			const metaMenu = this.metaMenuElement.nativeElement;
			const languageMenu = this.languageMenuElement.nativeElement;

			// Get the start bounding boxes of the elements we want to animate
			const mobileHeaderBoundingStart =
				mobileHeader.getBoundingClientRect();
			const mobileMenuBoundingStart = mobileMenu.getBoundingClientRect();
			const metaMenuBoundingStart = metaMenu.getBoundingClientRect();
			const languageMenuBoundingStart =
				languageMenu.getBoundingClientRect();

			// Get the end bounding boxes of the elements we want to animate
			this.menuIsOpen = true;
			const mobileMenuBoundingEnd = mobileMenu.getBoundingClientRect();
			const metaMenuBoundingEnd = metaMenu.getBoundingClientRect();
			this.menuIsOpen = initialMenuIsOpen;
			this.languageIsOpen = true;
			const languageMenuBoundingEnd =
				languageMenu.getBoundingClientRect();
			this.languageIsOpen = initialLanguageIsOpen;

			// Check if a top banner exists and adjust the height of the mobile menu accordingly
			const topBanner = this.document.querySelector('#top-banner');
			this.topBannerHeight =
				topBanner?.getBoundingClientRect().height || 0;

			// Calculate the delta Y for the animations
			this.mobileHeaderDeltaY =
				mobileHeaderBoundingStart.bottom -
				mobileHeaderBoundingStart.top;
			this.mobileMenuDeltaY =
				mobileMenuBoundingStart.top - mobileMenuBoundingEnd.top;
			this.metaMenuDeltaY =
				metaMenuBoundingStart.top - metaMenuBoundingEnd.top;
			this.languageMenuDeltaY =
				languageMenuBoundingStart.top - languageMenuBoundingEnd.top;
			this.ctaMenuDeltaY =
				metaMenuBoundingStart.top - metaMenuBoundingEnd.top;

			// Set the fixed height of the mobile menu so the animation looks better
			const menuHeight =
				'calc(100% - ' +
				(metaMenuBoundingStart.bottom -
					metaMenuBoundingStart.top +
					(mobileHeaderBoundingStart.bottom -
						mobileHeaderBoundingStart.top) +
					this.topBannerHeight) +
				'px)';

			mobileMenu.style.height = menuHeight;
		}
	}
	/* v8 ignore stop */

	toggleMenu() {
		/* v8 ignore start */
		if (isPlatformBrowser(this.platformId)) {
			/* v8 ignore stop */
			// Reset scroll position to the top when opening the menu
			window.scrollTo(0, 0);

			this.menuIsOpen = !this.menuIsOpen;
			GeneralHelper.bodyLock(this.menuIsOpen, window.scrollY);

			if (this.languageIsOpen && this.menuIsOpen) {
				this.toggleLanguageMenu();
			}
			/* v8 ignore start */
			if (!this.mobileMenuAnimation) {
				/* v8 ignore stop */
				this.mobileMenuAnimation =
					this.mobileMenuElement.nativeElement.animate(
						[
							{
								transformOrigin: 'top left',
								transform: `translateY(${this.mobileMenuDeltaY + this.mobileHeaderDeltaY + this.topBannerHeight}px)`,
							},
						],
						this.menuAnimation,
					);

				this.mobileMenuAnimation.pause();
			}

			if (this.menuIsOpen) {
				this.mobileMenuAnimation.play();
			} else {
				this.mobileMenuAnimation.reverse();
				/* v8 ignore start */
				this.mobileMenuAnimation.onfinish = () => {
					this.mobileMenuAnimation.cancel();
					this.mobileMenuAnimation = null;
				};
				/* v8 ignore stop */
			}

			this.metaMenuElement.nativeElement.animate(
				[
					{
						transformOrigin: 'top left',
						transform: `translateY(${
							!this.menuIsOpen
								? '100%'
								: this.metaMenuDeltaY + 'px'
						})`,
					},
				],
				this.menuAnimation,
			);

			this.updateBackgroundState();
		}
	}

	toggleLanguageMenu() {
		this.languageIsOpen = !this.languageIsOpen;

		if (this.languageIsOpen && this.menuIsOpen) {
			this.toggleMenu();
		}

		this.languageMenuElement.nativeElement.animate(
			[
				{
					transformOrigin: 'top left',
					transform: `translateY(${
						!this.languageIsOpen
							? '100%'
							: this.languageMenuDeltaY + 'px'
					})`,
				},
			],
			this.menuAnimation,
		);
	}

	toggleCtaMenu() {
		this.ctaIsOpen = !this.ctaIsOpen;

		if (this.ctaIsOpen && this.menuIsOpen) {
			this.toggleMenu();
		}

		this.ctaMenuElement.nativeElement.animate(
			[
				{
					transformOrigin: 'top left',
					transform: `translateY(${
						!this.ctaIsOpen ? '100%' : this.ctaMenuDeltaY + 'px'
					})`,
				},
			],
			this.menuAnimation,
		);
	}

	switchLanguage(language: string) {
		this.languageSwitch.emit(language);
	}

	closeMobileMenu(): void {
		if (this.menuIsOpen) {
			this.toggleMenu();
			this.languageIsOpen = false;
			this.ctaIsOpen = false;
		}
	}

	updateBackgroundState() {
		const shouldShow = !this.isNegativeHeader() || this.menuIsOpen;

		if (this.backgroundTimeoutId !== null) {
			clearTimeout(this.backgroundTimeoutId);
			this.backgroundTimeoutId = null;
		}

		if (shouldShow && !this.showBackground) {
			this.showBackground = true;
		} else if (!shouldShow && this.showBackground) {
			// Solve the issue with internal links not picking up negative header styles by delaying the hiding of the background until after the animation is done
			// TODO: Fix in https://new-story.atlassian.net/browse/VPL-43466
			this.backgroundTimeoutId = setTimeout(() => {
				this.backgroundTimeoutId = null;
				this.showBackground = false;
				this.cd.markForCheck();
			}, duration);
		}
	}
}
